import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Hook for detecting when an external script is ready (e.g. tag managers, analytics, etc.)
 *
 * @param {() => boolean} checkReady - Must return `true` when the script is available and ready to use
 * @param {object} [options]
 * @param {string} [options.eventToListenFor] - Optional event name (e.g. 'tealium_loaded')
 * @param {number} [options.intervalMs=500] - How often to check, in ms
 * @param {number} [options.maxDurationS=60] - Max time to wait, in seconds
 * @param {Function} [options.onReady] - Called once when script becomes ready
 * @param {(duration: number) => void} [options.onTimeout] - Called if script isn't ready in time
 * @returns {boolean} Whether the script is ready
 */
export function useExternalScriptReady(
  checkReady,
  {
    eventToListenFor,
    intervalMs = 500,
    maxDurationS = 60,
    onReady,
    onTimeout,
  } = {},
) {
  const [ready, setReady] = useState(checkReady);
  const hasTriggeredReady = useRef(false);

  // using stable refs to prevent re-renders and re-checks when dependencies change
  // @see https://www.epicreact.dev/the-latest-ref-pattern-in-react
  const checkReadyRef = useRef(checkReady);
  const onReadyRef = useRef(onReady);
  const onTimeoutRef = useRef(onTimeout);

  // Keep refs updated with latest callbacks
  useLayoutEffect(() => {
    checkReadyRef.current = checkReady;
    onReadyRef.current = onReady;
    onTimeoutRef.current = onTimeout;
  }, [checkReady, onReady, onTimeout]);

  useEffect(() => {
    if (ready) {
      if (!hasTriggeredReady.current) {
        onReadyRef.current?.();
        hasTriggeredReady.current = true;
      }
      return;
    }

    const cleanup = () => {
      clearInterval(pollIntervalId);
      if (eventToListenFor) {
        window.removeEventListener(eventToListenFor, checkIfReady);
      }
    };

    // set a timeout for how long we'll check for readiness
    const timeoutAt = Date.now() + maxDurationS * 1000;

    const checkIfReady = () => {
      if (checkReadyRef.current()) {
        setReady(true);
      } else if (Date.now() >= timeoutAt) {
        // we've checked long enough, so now stop checking and call onTimeout
        onTimeoutRef.current?.(maxDurationS);
        cleanup();
      }
    };

    // listen for the event that signals the script is ready
    if (eventToListenFor) {
      window.addEventListener(eventToListenFor, checkIfReady);
    }

    // start polling for readiness
    const pollIntervalId = setInterval(checkIfReady, intervalMs);

    // try once immediately in case the script is already ready
    checkIfReady();

    return cleanup;
  }, [ready, eventToListenFor, intervalMs, maxDurationS]);

  return ready;
}
